/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { ApolloError, useLazyQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { message } from 'antd';
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';

import Loader, { ILoaderType } from '@/components/Loader';
import ErrorPage from '@/pages/ErrorPage';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { selectDepartment } from '@/stores/slices/department';
import { userSelector } from '@/stores/slices/user';
import AppConstants from '@/utils/AppConstants';
import {
  Department,
  GetDepartmentQuery,
  Organization,
  useGetUserQuery,
  useListDepartmentsLazyQuery,
  useSetUserPreferencesMutation
} from '@/generated/graphql';
import {
  DEPARTMENT_SUBMODULE_LIST,
  DepartmentSubmodules
} from '@/utils/routes';
import { isEmpty } from 'lodash';

type ContextDepartment = Department | null;

interface App {
  currentDepartment: Department;
  setDepartment: (dept: Department, subModule?: string) => void;
  departmentId?: string;
  loading?: boolean;
  error?: Error;
  username: string | null;
  organization: Organization | null;
}

const AppContext = createContext<App>(null!);

export function AppProvider(props: { children: ReactNode }) {
  const [initialized, setInitialized] = useState(false);
  const [departmentFetchError, setDepartmentFetchError] = useState<
    string | Error | ApolloError
  >();
  const dispatch = useAppDispatch();
  const urlParams = useParams();
  const location = useLocation();

  const navigate = useNavigate();
  const [currentDepartment, setCurrentDepartment] = useState<
    ContextDepartment | undefined
  >();
  const [getUserData, setGetUserData] = useState<{
    username: string;
    organization: Organization | null;
  } | null>(null);
  const [fetch, { error }] = useLazyQuery<GetDepartmentQuery>(
    AppConstants.APIS.DEPARTMENT.GET_DEPARTMENT(),
    {
      variables: { id: urlParams.departmentId }
    }
  );
  const [setUserPreferences] = useSetUserPreferencesMutation();
  const { data: userData, loading: userLoading } = useGetUserQuery({
    onError(error) {
      console.error(error.message);
      message.error('Failed to fetch user details');
    }
  });

  const { username, organization } = useAppSelector(userSelector);

  const [fetchDepartments, { error: errorFetchingDepartments }] =
    useListDepartmentsLazyQuery({
      onError(error) {
        captureException(error, {
          extra: {
            message: 'Failed to fetch department details'
          }
        });
        message.error(error.message);
      }
    });

  const setDept = useCallback(
    async (newDepartment: Department) => {
      setCurrentDepartment(newDepartment);
      setUserPreferences({
        variables: {
          input: {
            lastUsedDepartmentId: newDepartment.id
          }
        }
      });

      dispatch(selectDepartment(newDepartment));
      message.success('Department changed successfully');

      if (location.pathname.startsWith('/department/')) {
        const subModule = location.pathname.split('/')[3];
        const redirectPath = generatePath(
          '/department/:departmentId/:subModule',
          {
            departmentId: newDepartment.id,
            // the third segment of the path is the subModule in case of department routes
            subModule: DEPARTMENT_SUBMODULE_LIST.includes(
              subModule as DepartmentSubmodules
            )
              ? subModule
              : DepartmentSubmodules.PLAYGROUND
          }
        );
        navigate({ pathname: redirectPath });
      }
    },
    [location.pathname, navigate, dispatch]
  );

  const getDept = useCallback(
    async ({
      departmentId,
      fetchFirst = false
    }: {
      departmentId?: string | null;
      fetchFirst?: boolean;
    }) => {
      try {
        let department: Department | null = null;
        if (fetchFirst) {
          const { data } = await fetchDepartments({
            variables: { limit: 1 }
          });
          department = data?.listDepartments?.items?.length
            ? data?.listDepartments?.items[0]
            : null;
          if (!department) {
            message.info('No department found');
          }
        } else {
          const { data, error: departmentFetchError } = await fetch({
            variables: { id: departmentId }
          });

          if (departmentFetchError) {
            console.log(departmentFetchError);
          }

          department = (data?.getDepartment as Department) || null;
          if (department === null) {
            message.error('Department not found');
            getDept({
              fetchFirst: true
            });
          }
        }
        setCurrentDepartment(department || null);
        // backward compatibility for components using redux-state slice
        dispatch(selectDepartment(department || null));
      } catch (err: any) {
        setDepartmentFetchError(err);
      }
    },
    [fetch, fetchDepartments, dispatch]
  );

  useEffect(() => {
    if (username && !isEmpty(userData)) {
      if (urlParams.departmentId) {
        setUserPreferences({
          variables: {
            input: {
              lastUsedDepartmentId: urlParams.departmentId
            }
          }
        });
      }

      const deptId =
        urlParams.departmentId ||
        userData?.getUser?.preferences?.lastUsedDepartmentId;

      getDept({
        departmentId: deptId,
        fetchFirst: !deptId
      });
    }
  }, [username, urlParams.departmentId, getDept, userData]);

  useEffect(() => {
    if (!organization?.loading && username) {
      setGetUserData({
        username,
        organization: organization.data || null
      });
    }
  }, [organization, username]);

  useEffect(() => {
    if (!organization?.loading) {
      const validOrg =
        getUserData?.organization ||
        getUserData?.organization === null ||
        organization.error;
      const validDepartment =
        currentDepartment || currentDepartment === null || departmentFetchError;
      if (getUserData?.username && validOrg && validDepartment) {
        setInitialized(true);
      }
    }
  }, [
    getUserData?.username,
    getUserData?.organization,
    currentDepartment,
    departmentFetchError,
    organization.error,
    organization.loading
  ]);

  useEffect(() => {
    if (!organization?.loading && username) {
      setGetUserData({
        username,
        organization: organization.data || null
      });
    }
  }, [organization, username]);

  useEffect(() => {
    if (!organization?.loading) {
      const validOrg =
        getUserData?.organization ||
        getUserData?.organization === null ||
        organization.error;
      const validDepartment =
        currentDepartment || currentDepartment === null || departmentFetchError;
      if (getUserData?.username && validOrg && validDepartment) {
        setInitialized(true);
      }
    }
  }, [
    getUserData?.username,
    getUserData?.organization,
    currentDepartment,
    departmentFetchError,
    organization.error,
    organization.loading
  ]);

  const contextVal = useMemo(
    () => ({
      currentDepartment: currentDepartment!,
      setDepartment: setDept,
      loading: !initialized,
      error: error || errorFetchingDepartments || organization.error,
      departmentId: urlParams?.departmentId,
      username: getUserData?.username!,
      organization: getUserData?.organization!
    }),
    [
      currentDepartment,
      error,
      setDept,
      urlParams?.departmentId,
      errorFetchingDepartments,
      initialized,
      organization.error,
      getUserData?.username,
      getUserData?.organization
    ]
  );

  if (organization.loading || userLoading || !initialized) {
    return (
      <div>
        <Loader type={ILoaderType.FULL_PAGE} message="Loading application..." />
      </div>
    );
  }

  const errorMsg = error || departmentFetchError;
  if (errorMsg) {
    return <ErrorPage error={errorMsg} />;
  }

  return (
    <AppContext.Provider value={contextVal}>
      {props.children}
    </AppContext.Provider>
  );
}

export function useApp() {
  return useContext<App>(AppContext);
}
